import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useQueries, useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame, DataTable, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import type { WindowDays } from "./api";
import { useControls, type Controls } from "./controls";
import { formatDay, formatCount } from "./format";
import { getCode, getCodes, getScans, PLACEMENTS, qrCodeKey, qrCodesKey, qrReadMessage, qrScansKey, type QrCodeSummary, type QrScanRow } from "./qr-api";
import { MultiChips, numeric, S_CODE, S_CODES, S_FUNNEL, S_GRAIN, S_PLACE, str } from "./qr-controls";
import {
  asList,
  codeDetailSearch,
  countryName,
  deviceLabel,
  funnelOf,
  mergeBreakdown,
  OTHER_KEY,
  parseGrain,
  parseScanDays,
  periodLabel,
  placementLabel,
  placementsLabel,
  scansByCode,
  scansByPeriod,
  scansSplit,
  SCAN_DAYS,
  topWithOther,
} from "./qr-format";

// The QR Scans tab (spec: os-portal specs/qr-codes-module.md V4). Read only. Every figure is the
// Worker's (`GET /v1/qr/scans`, transparency spec S): it rolls days into weeks and months and owns
// the unique-scanner counts. This tab only pivots the rows it is sent into charts and a table.

const WINDOW_SPEC: FilterSpec = {
  name: "days",
  label: "Window",
  kind: "choice",
  options: SCAN_DAYS.map((value) => ({ value, label: `${value} days` })),
};

const GRAIN_SPEC: FilterSpec = {
  name: S_GRAIN,
  label: "Grain",
  kind: "choice",
  options: [
    { value: "day", label: "Day" },
    { value: "week", label: "Week" },
    { value: "month", label: "Month" },
  ],
};

/** Series colours come from the brand token layer; none is a raw hex. */
const SERIES_COLOURS = [
  "var(--color-ks-purple)",
  "var(--color-ks-ink)",
  "var(--color-ks-purple-2)",
  "var(--color-ks-pass)",
  "var(--color-ks-warn)",
  "var(--color-ks-accent)",
  "var(--color-ks-grey-1)",
  "var(--color-ks-purple-1)",
];
const OTHER_COLOUR = "var(--color-ks-grey-3)";

const PLACEMENT_OPTIONS = PLACEMENTS.map((value) => ({ value, label: placementLabel(value) }));
const GRAIN_NOUN = { day: "day", week: "week", month: "month" } as const;

type CodeRow = { id: string; label: string; placements: string; scans: number; unique: number; last_scan_day: string | null };

export function QrScansTab({ areaName }: { areaName: string }) {
  const controls = useControls();
  const days = parseScanDays(controls.values.days);
  const grain = parseGrain(controls.values[S_GRAIN]);
  const pickedCodes = asList(controls.values[S_CODES]);
  const pickedPlaces = asList(controls.values[S_PLACE]);
  const funnel = str(controls.values[S_FUNNEL]);

  const codesQuery = useQuery({ queryKey: qrCodesKey(30), queryFn: () => getCodes(30) });
  const all = codesQuery.data?.data.codes ?? [];
  const selected = all.filter(
    (code) =>
      (pickedCodes.length === 0 || pickedCodes.includes(code.id)) &&
      (pickedPlaces.length === 0 || code.placements.some((place) => pickedPlaces.includes(place))) &&
      (funnel === "" || funnelOf(code.label) === funnel),
  );
  const narrowed = pickedCodes.length > 0 || pickedPlaces.length > 0 || funnel !== "";
  // No narrowing asks for every code (the Worker's default); a narrowing names exactly the codes chosen.
  const ids = narrowed ? selected.map((code) => code.id) : [];
  const nothingMatches = narrowed && codesQuery.isSuccess && selected.length === 0;

  const scans = useQuery({
    queryKey: qrScansKey(days, grain, ids),
    queryFn: () => getScans(days, grain, ids),
    enabled: codesQuery.isSuccess && !nothingMatches,
  });

  const funnels = [...new Set(all.map((code) => funnelOf(code.label)))].sort();
  const funnelSpec: FilterSpec = {
    name: S_FUNNEL,
    label: "Funnel",
    kind: "choice",
    options: [{ value: "", label: "All" }, ...funnels.map((value) => ({ value, label: value }))],
  };
  const setList = (key: string) => (next: string[]) => controls.onChange({ ...controls.values, [key]: next });

  const failure = codesQuery.isError ? codesQuery.error : scans.isError ? scans.error : null;
  const rows: QrScanRow[] = scans.data?.data.rows ?? [];
  const notes = (scans.data?.meta as { notes?: string | string[] } | undefined)?.notes;
  const noteText = Array.isArray(notes) ? notes.join(" ") : (notes ?? "");

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end gap-4">
        <FilterBar
          layout="segmented"
          filters={[WINDOW_SPEC, GRAIN_SPEC, funnelSpec]}
          values={{ ...controls.values, days: String(days), [S_GRAIN]: grain }}
          onChange={controls.onChange}
        />
        <MultiChips
          label="Placement (any of)"
          options={PLACEMENT_OPTIONS}
          selected={pickedPlaces}
          onChange={setList(S_PLACE)}
        />
      </div>
      <MultiChips
        label="Codes (any of)"
        options={all.map((code) => ({ value: code.id, label: code.label }))}
        selected={pickedCodes}
        onChange={setList(S_CODES)}
      />

      {failure ? (
        <Alert>
          <AlertTitle>QR Scans unavailable</AlertTitle>
          <AlertDescription>{qrReadMessage(failure, areaName)}</AlertDescription>
        </Alert>
      ) : nothingMatches ? (
        <p className="text-base text-ks-ink">No codes match these filters.</p>
      ) : (
        <>
          <Figures rows={rows} pending={scans.isPending} note={noteText} />
          <TotalsChart rows={rows} grain={grain} pending={scans.isPending} />
          <SplitChart rows={rows} grain={grain} codes={all} pending={scans.isPending} />
          <CodeTable rows={rows} codes={selected} all={all} narrowed={narrowed} pending={scans.isPending || codesQuery.isPending} />
          <CodeBreakdown controls={controls} days={days} codes={narrowed ? selected : all} />
        </>
      )}
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums text-ks-ink">{value}</dd>
    </div>
  );
}

function Figures({ rows, pending, note }: { rows: QrScanRow[]; pending: boolean; note: string }) {
  const totals = scansByCode(rows);
  const scans = totals.reduce((sum, row) => sum + row.scans, 0);
  const unique = totals.reduce((sum, row) => sum + row.unique_scanners, 0);
  return (
    <section aria-label="Totals" className="flex flex-col gap-2">
      <dl className="grid grid-cols-2 gap-4 sm:max-w-md">
        <Figure label="Scans" value={pending ? "–" : formatCount(scans)} />
        <Figure label="Unique scanners" value={pending ? "–" : formatCount(unique)} />
      </dl>
      {note && <p className="text-xs text-muted-foreground">{note}</p>}
    </section>
  );
}

const tooltipStyle = {
  background: "var(--color-ks-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius-ks-sm)",
  fontSize: 12,
};

function TotalsChart({ rows, grain, pending }: { rows: QrScanRow[]; grain: "day" | "week" | "month"; pending: boolean }) {
  const data = scansByPeriod(rows, grain);
  return (
    <ChartFrame
      title={`Scans and unique scanners per ${GRAIN_NOUN[grain]}`}
      subtitle="For the codes chosen above. A period with no scans is a zero."
      titleVisible
      pending={pending}
      pointCount={data.length}
      emptyMessage="No scans in this window for these codes."
    >
      <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-ks-grey-4)" />
        <XAxis dataKey="period" tick={{ fontSize: 12 }} stroke="var(--color-ks-grey-1)" tickFormatter={(period: string) => periodLabel(period, grain)} />
        <YAxis tick={{ fontSize: 12 }} stroke="var(--color-ks-grey-1)" allowDecimals={false} width={40} />
        <Tooltip labelFormatter={(period) => periodLabel(String(period), grain)} contentStyle={tooltipStyle} />
        <Line
          type="linear"
          dataKey="scans"
          name="Scans"
          stroke="var(--color-ks-purple)"
          strokeWidth={2}
          dot={{ r: 3, fill: "var(--color-ks-purple)", stroke: "var(--color-ks-purple)" }}
          isAnimationActive={false}
        />
        <Line
          type="linear"
          dataKey="unique_scanners"
          name="Unique scanners"
          stroke="var(--color-ks-grey-1)"
          strokeWidth={2}
          dot={{ r: 3, fill: "var(--color-ks-grey-1)", stroke: "var(--color-ks-grey-1)" }}
          isAnimationActive={false}
        />
      </LineChart>
    </ChartFrame>
  );
}

function SplitChart({ rows, grain, codes, pending }: { rows: QrScanRow[]; grain: "day" | "week" | "month"; codes: QrCodeSummary[]; pending: boolean }) {
  const split = scansSplit(rows, grain);
  const name = (id: string) => codes.find((code) => code.id === id)?.label ?? id;
  const keys = split.hasOther ? [...split.keys, OTHER_KEY] : split.keys;
  return (
    <ChartFrame
      title={`Scans per ${GRAIN_NOUN[grain]} by code`}
      subtitle="One line per code. The 8 busiest are shown and the rest are grouped as Other."
      titleVisible
      pending={pending}
      pointCount={split.data.length}
      emptyMessage="No scans in this window for these codes."
    >
      <LineChart data={split.data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-ks-grey-4)" />
        <XAxis dataKey="period" tick={{ fontSize: 12 }} stroke="var(--color-ks-grey-1)" tickFormatter={(period: string) => periodLabel(period, grain)} />
        <YAxis tick={{ fontSize: 12 }} stroke="var(--color-ks-grey-1)" allowDecimals={false} width={40} />
        <Tooltip labelFormatter={(period) => periodLabel(String(period), grain)} contentStyle={tooltipStyle} />
        {keys.map((key, index) => (
          <Line
            key={key}
            type="linear"
            dataKey={key}
            name={key === OTHER_KEY ? "Other" : name(key)}
            stroke={key === OTHER_KEY ? OTHER_COLOUR : SERIES_COLOURS[index % SERIES_COLOURS.length]}
            strokeWidth={2}
            dot={{ r: 3, fill: key === OTHER_KEY ? OTHER_COLOUR : SERIES_COLOURS[index % SERIES_COLOURS.length] }}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ChartFrame>
  );
}

const CODE_COLUMNS: ColumnDef<CodeRow, unknown>[] = [
  { id: "name", header: "Name", accessorFn: (row) => row.label },
  { id: "placements", header: "Placements", accessorFn: (row) => row.placements },
  { id: "scans", header: "Scans", accessorFn: (row) => formatCount(row.scans), ...numeric },
  { id: "unique", header: "Unique scanners", accessorFn: (row) => formatCount(row.unique), ...numeric },
  { id: "last_scan", header: "Last scan", accessorFn: (row) => (row.last_scan_day ? formatDay(row.last_scan_day) : "–") },
];

function CodeTable({ rows, codes, all, narrowed, pending }: { rows: QrScanRow[]; codes: QrCodeSummary[]; all: QrCodeSummary[]; narrowed: boolean; pending: boolean }) {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { area?: string; module?: string };
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const totals = new Map(scansByCode(rows).map((row) => [row.code_id, row]));
  const shown = narrowed ? codes : all;
  const table: CodeRow[] = shown
    .map((code) => ({
      id: code.id,
      label: code.label,
      placements: placementsLabel(code.placements),
      scans: totals.get(code.id)?.scans ?? 0,
      unique: totals.get(code.id)?.unique_scanners ?? 0,
      last_scan_day: code.last_scan_day,
    }))
    .sort((a, b) => b.scans - a.scans || a.label.localeCompare(b.label));
  const open = (id: string) => {
    void navigate({
      to: "/app/$area/$module/$tab",
      params: { area: params.area ?? "customer_acquisition", module: params.module ?? "landing_pages", tab: "qr_codes" } as never,
      search: codeDetailSearch(search, id) as never,
    });
  };
  return (
    <section aria-label="Scans by code" className="flex flex-col gap-2">
      <h3 className="text-base font-semibold text-ks-ink">Scans by code</h3>
      <DataTable
        columns={CODE_COLUMNS}
        rows={table}
        pending={pending}
        page={{ pageIndex: 0, pageSize: Math.max(table.length, 1), rowCount: table.length }}
        onPageChange={() => {}}
        onRowClick={(row) => open(row.id)}
        emptyMessage="No codes match these filters."
        caption="Scans per code. Open a row to see and change the code."
      />
    </section>
  );
}

const NAME_COLUMN = (header: string): ColumnDef<{ label: string; scans: number }, unknown> => ({
  id: "name",
  header,
  accessorFn: (row) => row.label,
});
const SCANS_COLUMN: ColumnDef<{ label: string; scans: number }, unknown> = {
  id: "scans",
  header: "Scans",
  accessorFn: (row) => formatCount(row.scans),
  ...numeric,
};

function BreakdownTable({ title, header, rows }: { title: string; header: string; rows: { label: string; scans: number }[] }) {
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-base font-semibold text-ks-ink">{title}</h4>
      <DataTable
        columns={[NAME_COLUMN(header), SCANS_COLUMN]}
        rows={rows}
        page={{ pageIndex: 0, pageSize: Math.max(rows.length, 1), rowCount: rows.length }}
        onPageChange={() => {}}
        emptyMessage="No scans in this window."
        caption={title}
      />
    </div>
  );
}

/** A share-of-scans pie: the 7 biggest slices, the rest as Other, colours from the brand tokens. */
function BreakdownPie({ title, rows }: { title: string; rows: { label: string; scans: number }[] }) {
  const data = topWithOther(rows, 7).filter((row) => row.scans > 0);
  const total = data.reduce((sum, row) => sum + row.scans, 0);
  const share = (value: number) => (total > 0 ? `${Math.round((value / total) * 100)}%` : "0%");
  return (
    <ChartFrame title={title} titleVisible pending={false} pointCount={data.length} emptyMessage="No scans in this window.">
      <PieChart>
        <Pie data={data} dataKey="scans" nameKey="label" innerRadius="45%" outerRadius="80%" isAnimationActive={false}>
          {data.map((row, index) => (
            <Cell key={row.label} fill={row.label === "Other" ? OTHER_COLOUR : SERIES_COLOURS[index % SERIES_COLOURS.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(value) => `${formatCount(Number(value))} (${share(Number(value))})`} contentStyle={tooltipStyle} />
        <Legend formatter={(label: string) => `${label} · ${share(data.find((row) => row.label === label)?.scans ?? 0)}`} />
      </PieChart>
    </ChartFrame>
  );
}

const ALL_CODES = "all";

/** Country and device for every code chosen above (All) or for one code, as pies over tables. */
function CodeBreakdown({ controls, days, codes }: { controls: Controls; days: number; codes: QrCodeSummary[] }) {
  const chosen = str(controls.values[S_CODE]);
  const one = codes.some((code) => code.id === chosen) ? chosen : "";
  const ids = one !== "" ? [one] : codes.map((code) => code.id);
  // The code read allows 7, 30 or 90 days; a year reads as the last 90.
  const readDays = (days === 365 ? 90 : days) as WindowDays;
  // The Worker reads country and device per code, so "All" sums each code's read here.
  const reads = useQueries({
    queries: ids.map((id) => ({ queryKey: qrCodeKey(id, readDays), queryFn: () => getCode(id, readDays) })),
  });
  if (codes.length === 0) return null;
  const failed = reads.find((read) => read.isError && !read.data);
  // A failed code no longer hides the rest: show what loaded and say how many could not be read.
  const settled = reads.every((read) => read.data !== undefined || read.isError);
  const missing = reads.filter((read) => read.isError && !read.data).length;
  const loaded = settled && reads.some((read) => read.data !== undefined);
  const details = reads.flatMap((read) => (read.data ? [read.data.data] : []));
  const byCountry = mergeBreakdown(details.map((data) => data.by_country.map((row) => ({ label: countryName(row.country), scans: row.scans }))));
  const byDevice = mergeBreakdown(details.map((data) => data.by_device.map((row) => ({ label: deviceLabel(row.device), scans: row.scans }))));
  const batches = one !== "" ? details[0]?.by_key : undefined;
  return (
    <section aria-label="Where scans come from" className="flex flex-col gap-3">
      <h3 className="text-base font-semibold text-ks-ink">Where scans come from</h3>
      <div className="flex flex-col gap-2">
        <Label htmlFor="qr-scans-code">Code</Label>
        <Select value={one === "" ? ALL_CODES : one} onValueChange={(next) => controls.onChange({ ...controls.values, [S_CODE]: next === ALL_CODES ? "" : next })}>
          <SelectTrigger id="qr-scans-code" data-nonmutating className="w-full max-w-md">
            <SelectValue placeholder="Pick a code…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_CODES}>All codes chosen above ({codes.length})</SelectItem>
            {codes.map((code) => (
              <SelectItem key={code.id} value={code.id}>
                {code.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {days === 365 && <p className="text-xs text-muted-foreground">Country and device cover the last 90 days.</p>}
      </div>
      {loaded ? (
        <>
          {missing > 0 && <p className="text-xs text-muted-foreground">{missing} of {ids.length} codes could not be read and are left out.</p>}
          <div className="grid gap-6 lg:grid-cols-2">
            <BreakdownPie title="Devices" rows={byDevice} />
            <BreakdownPie title="Countries" rows={byCountry} />
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <BreakdownTable title="By country" header="Country" rows={topWithOther(byCountry)} />
            <BreakdownTable title="By device" header="Device" rows={topWithOther(byDevice)} />
            {batches !== undefined && (
              <BreakdownTable title="By batch" header="Batch" rows={batches.map((row) => ({ label: row.key, scans: row.scans }))} />
            )}
          </div>
        </>
      ) : failed && settled ? (
        <p className="text-sm text-ks-ink">{qrReadMessage(failed.error, "Customer Acquisition")}</p>
      ) : (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}
    </section>
  );
}
